/* eslint-disable max-lines-per-function */
import type { ReactElement } from "react";
import { Label } from "../../../../shared/components/ui/label";
import { SPACING } from "../../../../shared/constants/spacing";
import { CountrySelect } from "../../../admin/bulk-edit/components/CountrySelect";
import { GenreMultiSelect } from "../../../admin/bulk-edit/components/GenreMultiSelect";
import { LanguageMultiSelect } from "../../../admin/bulk-edit/components/LanguageMultiSelect";
import { RatingSelect } from "../../../admin/bulk-edit/components/RatingSelect";

interface ContentDetailsStepProps {
  formData: {
    synopsis?: string;
    description?: string;
    runtime?: number;
    rating?: string;
    language?: string | string[];
    country?: string;
    genres?: string[];
  };
  onChange: (field: string, value: string | string[] | number) => void;
}

/**
 * Content Details Step - Genres, Rating, Extended Description
 */
export function ContentDetailsStep({ formData, onChange }: Readonly<ContentDetailsStepProps>): ReactElement {
  console.log("ContentDetailsStep formData:", formData);
  console.log("ContentDetailsStep genres:", formData.genres);

  // Normalize language to array format
  let languageArray: string[] = [];
  if (Array.isArray(formData.language)) {
    languageArray = formData.language;
  } else if (formData.language) {
    languageArray = [formData.language];
  }

  return (
    <div className={SPACING.forms.sectionSpacing}>
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Content Details</h2>
        <p className="text-sm text-neutral-400">
          Review and refine your film's details from TMDB or enter them manually.
        </p>
      </div>

      <form className={SPACING.rails.gap} onSubmit={(e) => e.preventDefault()}>
        {/* Synopsis & Description - Single Row */}
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-2">
            <Label htmlFor="synopsis">Synopsis / Logline</Label>
            <p className="text-xs text-neutral-500 pb-4">A brief overview of your film</p>
            <textarea
              id="synopsis"
              rows={6}
              placeholder="A short description of your film's plot..."
              value={formData.synopsis || ""}
              onChange={(e) => onChange("synopsis", e.target.value)}
              className={`w-full ${SPACING.forms.inputPadding} bg-black/20 border border-neutral-800 rounded-lg text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-lumi-accent focus:border-lumi-accent resize-none transition-colors`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Long Synopsis</Label>
            <p className="text-xs text-neutral-500 pb-4">Detailed information about themes, style, and context</p>
            <textarea
              id="description"
              rows={6}
              placeholder="Elaborate on themes, style, inspiration, or any additional context..."
              value={formData.description || ""}
              onChange={(e) => onChange("description", e.target.value)}
              className={`w-full ${SPACING.forms.inputPadding} bg-black/20 border border-neutral-800 rounded-lg text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-lumi-accent focus:border-lumi-accent resize-none transition-colors`}
            />
          </div>
        </div>

        {/* Language, Country & Genres - Single Row */}
        <div className="grid grid-cols-3 gap-8">
          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <p className="text-xs text-neutral-500 pb-4">Primary language(s) of the film</p>
            <LanguageMultiSelect
              value={languageArray}
              onChange={(codes: string[]) => onChange("language", codes)}
              containerClassName="flex items-center justify-start gap-2 w-full min-h-[40px] px-4 py-2 bg-black/20 border border-neutral-800 rounded-lg transition-colors"
              badgeClassName="inline-flex items-center gap-2 px-3 py-1 bg-lumi-accent/10 border border-lumi-accent text-lumi-accent text-sm rounded-full hover:bg-lumi-accent/20 transition-colors"
              comboboxTriggerClassName="w-6 h-6 p-0 border-none text-white hover:bg-lumi-accent/20 rounded transition-colors"
              searchInputClassName="!h-9 !border-none !shadow-none !outline-none !ring-0"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <p className="text-xs text-neutral-500 pb-4">Primary country of origin</p>
            <CountrySelect
              value={formData.country || null}
              onChange={(code: string | null) => onChange("country", code || "")}
              triggerClassName="w-full h-[48px] px-4 py-2 bg-black/20 border border-neutral-800 rounded-lg hover:bg-black/30 transition-colors"
              textClassName="text-sm text-white"
              placeholder="Select country"
              searchInputClassName="!h-9 !border-none !shadow-none !outline-none !ring-0"
            />
          </div>
          <div className="space-y-2">
            <Label>
              Genres <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-neutral-500 pb-4">Select all that apply</p>
            <GenreMultiSelect
              value={formData.genres || []}
              onChange={(genres: string[]) => onChange("genres", genres)}
              containerClassName="flex items-center justify-start gap-2 w-full min-h-[40px] px-4 py-2 bg-black/20 border border-neutral-800 rounded-lg transition-colors"
              badgeClassName="inline-flex items-center gap-2 px-3 py-1 bg-lumi-accent/10 border border-lumi-accent text-lumi-accent text-sm rounded-full hover:bg-lumi-accent/20 transition-colors"
              comboboxTriggerClassName="w-6 h-6 p-0 border-none text-white hover:bg-lumi-accent/20 rounded transition-colors"
              searchInputClassName="!h-9 !border-none !shadow-none !outline-none !ring-0"
            />
          </div>
        </div>

        {/* Runtime & Rating */}
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-2 pb-12">
            <Label htmlFor="runtime">Runtime</Label>
            <p className="text-xs text-neutral-500 pb-4">in minutes</p>
            <input
              id="runtime"
              type="number"
              placeholder="120"
              min="1"
              value={formData.runtime || ""}
              onChange={(e) => onChange("runtime", Number.parseInt(e.target.value) || 0)}
              className="w-full px-4 py-2.5 h-[48px] bg-black/20 border border-neutral-800 rounded-lg text-white placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-lumi-accent focus:border-lumi-accent transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rating">Content Rating</Label>
            <p className="text-xs text-neutral-500 pb-4">MPAA Rating</p>
            <RatingSelect
              value={formData.rating ?? null}
              onChange={(rating) => onChange("rating", rating ?? "")}
              triggerClassName="w-full h-[48px] px-4 py-2 bg-black/20 border border-neutral-800 rounded-lg hover:bg-black/30 transition-colors"
              textClassName="text-sm text-white"
              placeholder="Select rating"
              contentClassName="w-[var(--radix-select-trigger-width)] my-1"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
